import { useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { useCommand } from '@/app/commands';
import { orgKey } from '@/app/queryClient';
import { useOrgId } from '@/app/session';
import { Avatar, Button, EmptyState, Field, Modal, Section, Select, Skeleton, TextInput, Time } from '@/components/ui';
import { humanize } from '@/lib/format';
import { contactQuery } from '@/features/parties/PartyDrawers';
import { conversationsQuery, locationKeys, type Location } from './api';

type S = components['schemas'];

/** Most recent message time per contact across the location's threads. */
function lastContactByContact(items: readonly S['Conversation'][]): Map<string, string> {
  const out = new Map<string, string>();
  for (const c of items) {
    if (!c.last_message_at) continue;
    for (const p of c.participants) {
      if (!p.contact_id) continue;
      const prev = out.get(p.contact_id);
      if (!prev || prev < c.last_message_at) out.set(p.contact_id, c.last_message_at);
    }
  }
  return out;
}
type Role = S['LocationPartyRole'];
const ROLES: Role[] = ['owner', 'operator', 'broker', 'adviser', 'seller', 'utility', 'municipality', 'other'];

export function PeopleSection({ location: l, onOpenContact }: { location: Location; onOpenContact: (id: string) => void }) {
  const orgId = useOrgId();
  const [adding, setAdding] = useState(false);
  const details = useQueries({ queries: l.contacts.map((c) => contactQuery(orgId, c.contact_id)) });
  const conversations = useQuery(conversationsQuery(orgId, l.id));
  const lastContact = lastContactByContact(conversations.data?.items ?? []);
  return (
    <Section
      id="people"
      title="People"
      actions={
        <Button size="sm" variant="quiet" icon="plus" onClick={() => setAdding(true)} disabled={Boolean(l.deleted_at)}>
          Add person
        </Button>
      }
    >
      {l.contacts.length === 0 ? (
        <EmptyState compact title="No people" />
      ) : (
        <ul className="site-rows">
          {l.contacts.map((link, i) => {
            const c = details[i]?.data;
            const company = c?.affiliations[0]?.company_name ?? null;
            const last = lastContact.get(link.contact_id);
            const role = c?.job_title ?? humanize(link.role);
            return (
              <li key={`${link.contact_id}-${link.role}`}>
                <button type="button" className="site-row person-row" onClick={() => onOpenContact(link.contact_id)}>
                  <Avatar name={link.contact_display_name} company={company} size={32} />
                  <span className="site-row-main">
                    <span className="site-row-name name truncate">{link.contact_display_name}</span>
                    <span className="site-row-sub truncate">{c ? [role, company].filter(Boolean).join(', ') : <Skeleton width="60%" />}</span>
                  </span>
                  <span className="site-row-time">{last ? <Time value={last} /> : 'No mail'}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {adding ? <AddPersonDialog location={l} onClose={() => setAdding(false)} /> : null}
    </Section>
  );
}

function AddPersonDialog({ location: l, onClose }: { location: Location; onClose: () => void }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [role, setRole] = useState<Role>('owner');
  const [chosen, setChosen] = useState<S['ContactSummary'] | null>(null);
  // A person created on a failed attempt is reused on retry, never created twice.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const linked = new Set(l.contacts.map((c) => c.contact_id));
  const term = name.trim();
  const suggestions = useQuery({
    queryKey: orgKey(orgId, 'contacts', 'list', { q: term, limit: 6 }),
    queryFn: ({ signal }) => unwrap(api.GET('/api/v1/orgs/{orgId}/contacts', { params: { path: { orgId }, query: { q: term, limit: 6 } }, signal })),
    enabled: term.length >= 2 && !chosen,
    select: (p) => p.items.filter((c) => !linked.has(c.id)),
  });

  const cmd = useCommand({
    run: async () => {
      let contactId = chosen?.id ?? createdId;
      if (!contactId) {
        const created = await unwrap(
          api.POST('/api/v1/orgs/{orgId}/contacts', {
            params: { path: { orgId }, header: { 'Idempotency-Key': newIdempotencyKey() } },
            body: {
              display_name: term,
              job_title: title.trim() || undefined,
              channels: email.trim() ? [{ kind: 'email', value: email.trim(), is_primary: true, do_not_contact: false }] : [],
            },
          }),
        );
        contactId = created.id;
        setCreatedId(created.id);
      }
      return unwrap(
        api.PUT('/api/v1/orgs/{orgId}/locations/{locationId}/relationships', {
          params: { path: { orgId, locationId: l.id }, header: { 'If-Match': ifMatch(l.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: {
            companies: l.companies.map((c) => ({
              company_id: c.company_id,
              role: c.role,
              is_primary: c.is_primary,
              source_id: c.source_id,
              valid_from: c.valid_from,
              valid_to: c.valid_to,
            })),
            contacts: [
              ...l.contacts.map((c) => ({ contact_id: c.contact_id, role: c.role, is_primary: c.is_primary, source_id: c.source_id })),
              { contact_id: contactId, role, is_primary: l.contacts.length === 0 },
            ],
            reason: `Added ${chosen?.display_name ?? term} as ${humanize(role).toLowerCase()}`,
          },
        }),
      );
    },
    success: `${chosen?.display_name ?? term} added`,
    invalidate: [locationKeys.detail(orgId, l.id), orgKey(orgId, 'contacts')],
    onSuccess: onClose,
  });

  const emailInvalid = Boolean(email.trim()) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const submit = () => {
    if (!chosen && !term) {
      setError('Name required');
      return;
    }
    setError(null);
    if (!chosen && emailInvalid) return;
    cmd.mutate(undefined, { onError: () => void qc.invalidateQueries({ queryKey: locationKeys.detail(orgId, l.id) }) });
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!cmd.isPending}
      title="Add person"
      description={l.name}
      footer={
        <>
          <Button variant="quiet" onClick={onClose} disabled={cmd.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={cmd.isPending} onClick={submit}>
            Add person
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {chosen ? (
          <div className="loc-chosen">
            <div className="grow">
              <div className="strong">{chosen.display_name}</div>
              <div className="small faint">{[chosen.job_title, chosen.primary_email].filter(Boolean).join(', ')}</div>
            </div>
            <Button size="sm" variant="quiet" onClick={() => setChosen(null)}>
              Change
            </Button>
          </div>
        ) : (
          <>
            <Field label="Name" required error={error ?? undefined} hint="Search existing people">
              <TextInput autoFocus value={name} maxLength={200} onChange={(e) => setName(e.target.value)} autoComplete="off" />
            </Field>
            {suggestions.data?.length ? (
              <ul className="loc-suggest" aria-label="Existing people">
                {suggestions.data.map((c) => (
                  <li key={c.id}>
                    <button type="button" onClick={() => setChosen(c)}>
                      <span className="strong">{c.display_name}</span>
                      <span className="faint truncate">{[c.job_title, c.primary_email].filter(Boolean).join(', ')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <Field label="Email" error={emailInvalid ? 'Invalid email' : undefined}>
              <TextInput type="email" value={email} maxLength={320} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
            </Field>
            <Field label="Job title">
              <TextInput value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
            </Field>
          </>
        )}
        <Field label="Role at site">
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)} options={ROLES.map((r) => ({ value: r, label: humanize(r) }))} />
        </Field>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
